import FilterProvider from '@/context/filter/filter.context';
import { UserRoles } from '@/enums/enums';
import { getUserOptions } from '@/features/user/user.queries';
import { toCapitalize } from '@/utils/text-utils';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { zodSearchValidator } from '@tanstack/router-zod-adapter';
import { Plus, Settings } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import ContractTable from '@/components/custom-tables/contract.table';
import ContractInactiveModal from '@/components/modals/contract-inactive-modal';
import ContractCreateUpdateSheet from '@/components/sheets/contract-sheet/contract-create-update-sheet';
import ContractViewSheet from '@/components/sheets/contract-sheet/contract-view-sheet';

const VISIBLE_TO_ROLES = [UserRoles.Root, UserRoles.Admin, UserRoles.Secretary];

const contractRouteSchema = z.object({
  mode: z.enum(['create', 'update', 'view', 'status']).optional(),
  contract: z.number().optional(),
  contract_type: z.boolean().optional(),
});

export const Route = createFileRoute('/_app/(contracts)/contracts/')({
  validateSearch: zodSearchValidator(contractRouteSchema),
  component: Contracts,
  beforeLoad: async ({ context }) => {
    const { user } = await context.queryClient.ensureQueryData(getUserOptions);

    if (!VISIBLE_TO_ROLES.includes(user?.role as UserRoles)) {
      throw redirect({
        to: '/',
      });
    }
  },
});

function Contracts() {
  return (
    <div className='flex h-full flex-col gap-y-2 px-4 py-2'>
      <ContractsTabHeader />
      <ContractsTable />
    </div>
  );
}

function ContractsTabHeader() {
  const navigate = useNavigate();
  const { t } = useTranslation(['contract', 'common']);

  const createContract = () => {
    navigate({ to: '.', search: { mode: 'create' } });
  };

  const navigateToSettings = () => {
    navigate({ to: '/contracts/settings' });
  };

  return (
    <div className='flex flex-row justify-between py-4'>
      <div className='flex flex-col'>
        <span className='text-xl font-bold'>{t('page_title', { format: 'uppercase' })}</span>
        <span className='text-base font-light text-neutral-500'>{t('page_sub_title')}</span>
      </div>
      <div className='flex flex-row space-x-4'>
        <Button className='flex items-center gap-3' onClick={createContract} size='default'>
          <Plus strokeWidth={4} className='h-6 w-6' />
          {toCapitalize(t('contract', { ns: 'common' }))}
        </Button>
        <Button className='flex items-center gap-3' onClick={navigateToSettings} size='default'>
          <Settings className='h-6 w-6' />
          {toCapitalize(t('settings', { ns: 'common' }))}
        </Button>
      </div>
      <ContractCreateUpdateSheet />
      <ContractViewSheet />
      <ContractInactiveModal />
    </div>
  );
}

function ContractsTable() {
  return (
    <FilterProvider>
      <ContractTable />
    </FilterProvider>
  );
}
